import { Alert, Box, Breadcrumbs, Divider, IconButton, Link as MuiLink, Snackbar, SnackbarCloseReason, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { FormElement, FieldWrapper } from '@progress/kendo-react-form';
import { TextBox } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Hint, Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { useState } from 'react';
import { Link as RouterLink, useLoaderData } from 'react-router-dom';
import { fetchApi } from 'core/fetchApi';
import { UsersResponse } from './index';

type InviteUserResponse = {
  Success: boolean;
  Added: boolean;
  Message: string;
  ErrorMessage: string;
}

const NewUser = () => {
  const data = useLoaderData() as UsersResponse;
  const [snackbar, setSnackbar] = useState('');
  const [result, setResult] = useState<InviteUserResponse>();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [title, setTitle] = useState('');
  const [contactType, setContactType] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberExtension, setPhoneNumberExtension] = useState('');

  const handleCloseSnackbar = (_: Event | React.SyntheticEvent, reason: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbar('');
  };

  const _save = () => {
    fetchApi<InviteUserResponse>({
      url: 'InviteUser',
      method: 'POST',
      payload: {
        FirstName: firstName,
        LastName: lastName,
        EMailAddress: emailAddress,
        Title: title,
        ContactType: contactType,
        PhoneNumber: phoneNumber,
        PhoneNumberExtension: phoneNumberExtension,
      }
    })
      .then((response) => {
        if (!response.Success) {
          setSnackbar(response.ErrorMessage);
          return;
        }
        setResult(response);
      })
      .catch(() => {
        setSnackbar('Error adding user');
      });
  };

  if (result) {
    return <Box sx={{ maxWidth: 600 }}>
      <Alert severity={result.Added ? 'success' : 'info'}>{result.Message}</Alert>
      <br />
      <MuiLink component={RouterLink} to="/users" underline="hover">Back to Users</MuiLink>
    </Box>;
  }

  return <>
    <Box sx={{ maxWidth: 600, pb: 4 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1 }}>
        <MuiLink component={RouterLink} to="/users" underline="hover" color="inherit">Users</MuiLink>
        <Typography color="text.primary">Add User</Typography>
      </Breadcrumbs>
      <Divider sx={{ my: 2 }} />
      <FormElement>
        <FieldWrapper>
          <Label editorId="EMailAddress" className="k-form-label">
            Email Address
          </Label>
          <div className="k-form-field-wrap">
            <TextBox
              required
              autoFocus
              id="EMailAddress"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.value as string)}
              placeholder="name@yourcompany.com"
              type="email"
              maxLength={80}
            />
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="FirstName" className="k-form-label">
            First Name
          </Label>
          <div className="k-form-field-wrap">
            <TextBox
              required
              id="FirstName"
              value={firstName}
              onChange={(e) => setFirstName(e.value as string)}
              maxLength={20}
            />
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="LastName" className="k-form-label">
            Last Name
          </Label>
          <div className="k-form-field-wrap">
            <TextBox
              required
              id="LastName"
              value={lastName}
              onChange={(e) => setLastName(e.value as string)}
              maxLength={20}
            />
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="Title" className="k-form-label">
            Title
          </Label>
          <div className="k-form-field-wrap">
            <TextBox
              id="Title"
              value={title}
              onChange={(e) => setTitle(e.value as string)}
              maxLength={20}
            />
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="ContactType" className="k-form-label">
            Contact Type
          </Label>
          <div className="k-form-field-wrap">
            <DropDownList
              id="ContactType"
              data={data.ContactTypes}
              textField="Name"
              dataItemKey="ID"
              value={data.ContactTypes.find(x => x.ID === contactType) || null}
              onChange={(e) => setContactType(e.target.value.ID)}
            />
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="PhoneNumber" className="k-form-label">
            Phone Number
          </Label>
          <div className="k-form-field-wrap">
            <TextBox
              id="PhoneNumber"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.value as string)}
              placeholder="+13135551212"
              maxLength={51}
            />
            <Hint>Include the country code, for example +13135551212.</Hint>
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="PhoneNumberExtension" className="k-form-label">
            Phone Extension
          </Label>
          <div className="k-form-field-wrap">
            <TextBox
              id="PhoneNumberExtension"
              value={phoneNumberExtension}
              onChange={(e) => setPhoneNumberExtension(e.value as string)}
              maxLength={4}
            />
          </div>
        </FieldWrapper>
      </FormElement>
      <Divider sx={{ my: 2 }} />
      <Button themeColor="primary" onClick={_save}>Add</Button>
    </Box>
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      open={!!snackbar}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      message={snackbar}
      action={
        <IconButton size="small" aria-label="close" color="inherit" onClick={(e) => handleCloseSnackbar(e, 'timeout')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    />
  </>;
}

export default NewUser;
